import { Sort, Filter, ButtonDropdown, Search, Filters } from '@cimplico/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { processQueryString } from '../entities/utils';

const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'type', label: 'Type' },
    { id: 'updatedAt', label: 'Last Updated' },
    { id: 'activeWorkpapers', label: 'Open Workpapers' },
];

const filterOptions: Filters = [
    {
        type: 'checkbox',
        title: 'Type',
        options: [
            { active: false, label: 'Company', value: 'COMPANY' },
            { active: false, label: 'Partnership', value: 'PARTNERSHIP' },
            { active: false, label: 'Sole Trader', value: 'SOLE_TRADER' },
            { active: false, label: 'Trust', value: 'TRUST' },
            { active: false, label: 'Individual', value: 'INDIVIDUAL' },
            { active: false, label: 'Unit Trust', value: 'UNIT_TRUST' },
            { active: false, label: 'Estate', value: 'ESTATE' },
            { active: false, label: 'Self Managed Superannuation Fund', value: 'SELF_MANAGED_SUPERANNUATION_FUND' },
            { active: false, label: 'Superannuation Fund', value: 'SUPERANNUATION_FUND' },
            { active: false, label: 'Government Entity', value: 'GOVERNMENT_ENTITY' },
            { active: false, label: 'Club Or Society', value: 'CLUB_OR_SOCIETY' },
            { active: false, label: 'Not For Profit', value: 'NOT_FOR_PROFIT' },
            { active: false, label: 'Accounting Practice', value: 'ACCOUNTING_PRACTICE' },
            { active: false, label: 'Charity', value: 'CHARITY' },
            { active: false, label: 'Look Through Company', value: 'LOOK_THROUGH_COMPANY' },
            { active: false, label: 'S Corporation', value: 'S_CORPORATION' },
            { active: false, label: 'Other', value: 'OTHER' },
        ],
        id: 'type',
    },
    {
        type: 'checkbox',
        title: 'Billing Status',
        options: [
            { active: false, label: 'Active', value: 'true' },
            { active: false, label: 'Inactive', value: 'false' },
        ],
        id: 'billingActive',
    },
    {
        type: 'toggle',
        title: 'Archived',
        option: { active: false },
        id: 'isArchived',
    },
    { type: 'date', title: 'Last Updated', option: {}, id: 'updatedAt' },
    { type: 'numeric', title: 'Open Workpapers', option: {}, id: 'activeWorkpapers', isPercent: false },
    {
        type: 'checkbox',
        title: 'Provider',
        options: [
            { active: false, label: 'Xero', value: 'XERO' },
            { active: false, label: 'FYI', value: 'FYI' },
        ],
        id: 'provider',
    },
];

type EntitiesPrimaryActionsProps = {
    fyiGroupId?: number;
    fyiGroupName?: string;
    onFilterChange: (qs: string) => void;
    onSearchChange: (qs: string) => void;
    onSortChange: (qs: string) => void;
};

function EntitiesPrimaryActions({
    fyiGroupId,
    fyiGroupName,
    onFilterChange,
    onSearchChange,
    onSortChange,
}: EntitiesPrimaryActionsProps): JSX.Element {
    const navigate = useNavigate();
    const { id: groupId } = useParams();
    const { integrations, shortId } = useFirmContext();
    const fyiIntegration = Boolean(integrations.fyi?.cabinet) && fyiGroupId;
    const activeSubscription = useIsActiveSubscription();
    const { isExternalUser } = useIsFirmRole();

    function redirectToNewEntity(provider?: string): void {
        if (provider && fyiGroupId && fyiGroupName) {
            navigate(
                `/${shortId}/entities/new?provider=${provider}&groupId=${fyiGroupId}&groupName=${encodeURIComponent(
                    fyiGroupName
                )}&returnTo=/${shortId}/groups/${groupId}?tab=entities`
            );
        } else {
            navigate(`/${shortId}/entities/new?groupId=${groupId}&returnTo=/${shortId}/groups/${groupId}?tab=entities`);
        }
    }

    return (
        <div className="flex gap-4 items-center">
            <Search onChange={onSearchChange} placeholder="Search..." />
            <Sort
                onChange={({ queryString }): void => onSortChange(queryString)}
                sortOptions={sortOptions}
                enableV2Query
                defaultSortOption={'name'}
            />
            <Filter
                onChange={({ queryString }): void => onFilterChange(processQueryString(queryString))}
                filters={filterOptions}
                enableV2Query
            />
            <ButtonDropdown
                variant="primary"
                label="New Entity"
                onClick={(): void => redirectToNewEntity()}
                size="sm"
                disabled={!activeSubscription || isExternalUser}
            >
                {fyiIntegration && (
                    <ButtonDropdown.SecondaryAction
                        label="Add from FYI"
                        onClick={(): void => redirectToNewEntity('fyi')}
                        disabled={!activeSubscription || isExternalUser}
                    />
                )}
            </ButtonDropdown>
        </div>
    );
}
export default EntitiesPrimaryActions;
